import { usePageMeta } from "../seo";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { CtaBand } from "../components/BookCta";
import { Reveal } from "../components/motion";

const FAQS = [
    {
        q: "How does setup and breakdown work?",
        a: "We handle everything. Our team arrives 60–90 minutes before your reservation to style the full setup — low tables, floor cushions, rugs, LED candles and florals. When your celebration ends, simply walk away. We return for complete breakdown and leave the space exactly as we found it.",
    },
    {
        q: "What happens if the weather doesn't cooperate?",
        a: "Texas weather keeps us humble. We monitor forecasts closely in the days before your event. If rain or high winds are expected, you can reschedule once at no charge with 24 hours' notice, move to a covered location we've pre-approved, or convert to one of our indoor styled setups.",
    },
    {
        q: "Which areas do you serve?",
        a: "We're based in McKinney, Texas and serve the greater Dallas-Fort Worth area, including Dallas, Frisco, Plano, Allen, Prosper, Celina, The Colony, Richardson, Highland Park, Southlake and Fort Worth. Locations beyond 30 miles of McKinney may include a small travel fee.",
    },
    {
        q: "How far in advance should I book?",
        a: "We recommend booking 2–4 weeks ahead, especially for weekend proposals and spring/fall dates in DFW. Planning something sooner? Reach out anyway — we occasionally have last-minute openings and love a good surprise timeline.",
    },
    {
        q: "Can I customize colors, themes and florals?",
        a: "Absolutely — customization is our signature. While our brand palette leans dusty rose and soft blush, we design every escape around your story. Share your Pinterest board or vision in your inquiry and we'll build a custom mood board for your approval before the big day.",
    },
    {
        q: "Do you provide food and drinks?",
        a: "Our packages focus on full styling and design. You're welcome to bring your own refreshments and we'll style them beautifully into your tablescape — or ask about our trusted local catering partners when you inquire.",
    },
    {
        q: "Is there a deposit to reserve my date?",
        a: "Yes — a 50% deposit secures your date, with the balance due 48 hours before your event. Everything is handled simply and securely through our HoneyBook inquiry and booking system.",
    },
    {
        q: "Why floor cushions instead of chairs?",
        a: "It's the heart of our aesthetic. Low, lounge-style seating around handcrafted tables creates intimacy you simply can't get at a standard table — guests relax, lean in and stay awhile. It's also what makes every photo feel warm and editorial.",
    },
];

export default function Faq() {
    usePageMeta(
        "FAQ | Enchanting Escapes — McKinney & Dallas-Fort Worth Event Styling",
        "Setup logistics, weather policy, service area and booking timeline for Enchanting Escapes luxury event styling in McKinney and Dallas-Fort Worth."
    );

    return (
        <main data-testid="faq-page">
            <section className="py-20 sm:py-28">
                <div className="max-w-3xl mx-auto px-5 sm:px-8">
                    <Reveal className="text-center mb-16">
                        <p className="font-accent text-xs uppercase tracking-[0.25em] text-[#C18E8B] mb-4">
                            Frequently Asked
                        </p>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#2D230A] leading-tight">
                            Everything you need to know
                        </h1>
                        <p className="mt-5 text-[#6E5B3D] leading-relaxed">
                            Setup logistics, weather policy, service area and booking
                            timelines for celebrations across McKinney and Dallas-Fort Worth.
                        </p>
                    </Reveal>

                    <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
                        {FAQS.map((f, i) => (
                            <Reveal key={i} delay={i * 0.05} y={20}>
                                <AccordionItem
                                    value={`faq-${i}`}
                                    data-testid={`faq-item-${i}`}
                                    className="bg-white border border-[#E8C4C1] rounded-2xl px-6 overflow-hidden hover:border-[#C18E8B] transition-colors duration-300"
                                >
                                    <AccordionTrigger
                                        data-testid={`faq-trigger-${i}`}
                                        className="font-display text-lg sm:text-xl text-[#2D230A] hover:text-[#C18E8B] hover:no-underline text-left py-5"
                                    >
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-[#6E5B3D] leading-relaxed pb-6">
                                        {f.a}
                                    </AccordionContent>
                                </AccordionItem>
                            </Reveal>
                        ))}
                    </Accordion>
                </div>
            </section>

            <CtaBand
                heading="Still curious?"
                subtext="Send us a note through the inquiry form — we personally answer every question within 24 hours."
            />
        </main>
    );
}
